import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { Store } from '../../store/store';
import { Message, StoreNode } from '../../store';
import { assertNotNull, Nullable } from '../../core';
import { JobCatalogSource, JobModel } from '../../entities';
import { CatalogState } from '../../components/layout/catalogState';
import { Error, error, pageError } from '../../core/error';
import { Team } from '../../entities/team';
import { TeamMemberCatalogSource } from '../../entities/teamMemberCatalogSource';
import debounce from 'lodash/debounce';
import { getApiTeamError, isAgencyAccessRefused } from '../../api';
import type { ApiError } from '../../api/apiError';
import { TagSectionState } from '../../components/tags/tagSectionState';
import { VideoDetailsWindowState } from '../../components/jobs';
import { SearchBarState } from '../../components/search/searchBarState';

type Props = {

}

export type TeamDashboardPageParams = {
  teamId: string
}

export class TeamDashboardPageState
  extends StoreNode {

  constructor(store: Store, props?: Props) {
    super(store, props);
    makeObservable<this, 'manageContentOnPageMounted'>(this, {
      isLoading: observable,
      error: observable,
      teamManager: computed,
      teamId: observable,
      team: observable,
      handleTeamMatesModal: action,
      mounted: action,
      manageContentOnPageMounted: action,
      setPagesize: action,
      refreshTeam: action,
      refreshJobs: action,
      setError: action,
      setLoaded: action,
      reset: action,
    });

    this.jobCatalogSource = new JobCatalogSource(this.store, {
      teamId: () => this.teamId,
      pageSize: 20,
      jobFilter: (job: JobModel) => job.ownerTeamId === this.teamId
    });

    this.teamMemberCatalogSource = new TeamMemberCatalogSource(this.store, {
      teamId: () => this.teamId,
      pageSize: 6
    });

    this.jobCatalog = new CatalogState(this.store, {
      syncStatus: () => this.jobCatalogSource.syncStatus,
      isEndOfList: () => this.jobCatalogSource.isEndOfList
    });
    this.jobCatalog.listen((msg) => {
      switch (msg.type) {
        case 'scrollToBottom':
        case 'loadMore':
          this.jobCatalogSource.fetchMore();
          break;
      }
    });

    this.tagSectionState = new TagSectionState(this.store, {
      teamId: () => this.teamId,
      source: () => this.jobCatalogSource
    });

    this.searchBarState = new SearchBarState(this.store, { teamId: () => this.teamId, searchMode: 'idle' });
  }

  isLoading: boolean = true;
  error: Error | null = null;
  readonly searchBarState: SearchBarState;

  get teamManager() {
    return this.store.teamManager;
  }

  teamId: string | null = null;
  team: Team | null = null;

  readonly jobCatalogSource: JobCatalogSource;
  readonly jobCatalog: CatalogState;
  readonly tagSectionState: TagSectionState;

  readonly teamMemberCatalogSource: TeamMemberCatalogSource;

  handleTeamMatesModal() {
    if (this.team?.hasPermission('EditTeam'))
      return this.openTeamMembersEditWindow();

    if (this.team?.hasPermission('ViewTeam'))
      return this.openTeamMembersWindow();

    return false;

  }

  async mounted(params: TeamDashboardPageParams) {
    this.isLoading = true;
    this.searchBarState.reset();
    this.emit('Mounted', { teamId: params.teamId });
    this.bindExternalListeners();
    this.handlePageMountedDebounce(params);
  }

  unmounted() {
    this.store.uploadGuidedTraining.handleTeamDashboardUnmounted(this.teamId);
    this.store.adminGuidedTraining.handleTeamDashboardUnmounted(this.teamId);
    this.reset();
    this.unbindExternalListeners();
    this.emit('Unmounted');
  }

  private async manageContentOnPageMounted(params: TeamDashboardPageParams) {
    const { teamId } = params;
    this.teamId = teamId;

    const [, teamErr] = await this.store.teamManager.apiFetchTeam({ id: this.teamId });

    this.setTeam(this.teamManager.getTeam(this.teamId));

    if (!this.team) {
      const [teamRes, teamErr] = await this.store.api.getTeam({ id: this.teamId });

      if (teamErr)
        return this.setTeamError(teamErr);

      this.setTeam(new Team(this.store, teamRes?.getTeam));
    }

    // make sure that none of the requests failed and also we have a Team entity for the requested ID
    if (teamErr || !this.team)
      return this.setTeamError(teamErr);

    // we have a team so we can fetch the members, with the same defensive checks
    const [, membersErr] = await this.teamMemberCatalogSource.fetch();

    // const [, invitationsErr] = await team.apiFetchInvitations();
    if (membersErr) // || invitationsErr
      return this.setContentError(membersErr);

    const [, jobsErr] = await this.jobCatalogSource.fetch();
    if (jobsErr)
      return this.setContentError(jobsErr);

    // Fetch the tags for the corresponding team
    await this.tagSectionState.load();

    // everything ok
    this.setLoaded();
    const currentTeamId = this.teamId;
    if (!currentTeamId) return;

    const uploadGuidedTrainingResumed = this.store.uploadGuidedTraining
      .handleTeamDashboardMounted(currentTeamId);
    const adminGuidedTrainingResumed = !uploadGuidedTrainingResumed
      && this.store.adminGuidedTraining.handleTeamDashboardMounted(currentTeamId);

    const createFirstLibraryTrainingBlocks =
      this.store.createFirstLibraryGuidedTraining.shouldBlockNextLevel;
    const showOnboarding = this.store.storage.getLocal(
      'user.showOnboarding',
    ) && !this.store.ui.isMobile && !this.team?.hideUserDetails && !createFirstLibraryTrainingBlocks;

    const canEditTeam = !!this.team?.hasPermission('EditTeam');
    const showAdminTraining = this.store.adminGuidedTraining.canShowForTeam(canEditTeam, this.team);
    const accountGuidedTrainingPayload = {
      showUploadButtons: !!this.team?.hasPermission('CreateJob'),
      showTrainButton: showAdminTraining,
      teamId: currentTeamId,
    };
    const accountGuidedTrainingResumed = !createFirstLibraryTrainingBlocks
      && !uploadGuidedTrainingResumed
      && !adminGuidedTrainingResumed
      && this.store.accountGuidedTraining.resume(accountGuidedTrainingPayload);

    if (!uploadGuidedTrainingResumed && !adminGuidedTrainingResumed && !accountGuidedTrainingResumed && showOnboarding) {
      this.dispatch('openAccountGuidedTraining', accountGuidedTrainingPayload);

      this.store.storage.removeLocal(
        'user.showOnboarding',
      );
    }
  }

  private handlePageMountedDebounce = debounce((params: TeamDashboardPageParams) => this.manageContentOnPageMounted(params), 500);

  setPagesize(pageSize: number) {
    this.jobCatalogSource.setPagesizeValue(pageSize);
  }

  setTeam(team: Nullable<Team>) {
    runInAction(() => {
      this.team = team;
    })
  }

  private bindExternalListeners() {
    const { videoDetailsWindow } = this.store;

    videoDetailsWindow.listen(
      this.videoDetailsWindowListener);
  }

  private videoDetailsWindowListener = action(async (msg: Message<VideoDetailsWindowState>) => {
    switch (msg.type) {
      case 'JobUpdated': {
        this.isLoading = true;
        await this.refreshTeam();
        await this.refreshJobs();
        await this.tagSectionState.load();


        this.isLoading = false;
        break;
      }
    }
  });

  openTeamMembersWindow() {
    assertNotNull(this.teamId);
    const { team } = this;
    if (!team || team.isPublic) {
      // public team
      return;
    }

    this.store.teamMembersWindow.openTeam(this.teamId);
  }

  openTeamMembersEditWindow() {
    assertNotNull(this.teamId);
    const { team } = this;
    if (!team || team.isPublic) {
      // public team
      return;
    }

    this.store.teamMembersWindow.openTeamEdit(this.teamId);
  }

  async refreshTeam() {
    if (this.teamId)
      await this.store.teamManager.apiFetchTeam({ id: this.teamId });

    this.setTeam(this.teamManager.getTeam(this.teamId));
  }

  async refreshJobs() {
    return await this.jobCatalogSource.fetch();
  }

  setError(error?: Error) {
    if (!error)
      error = pageError();

    this.isLoading = false;
    this.error = error;
  }

  setLoaded() {
    this.isLoading = false;
    this.error = null;
  }

  private setTeamError(teamErr: ApiError | null) {
    // A refused library means the loaded profile is stale; refresh it so the sidebar drops it.
    if (isAgencyAccessRefused(teamErr))
      void this.store.agencyOperatorAccess.handleAccessRefused();
    return this.setError(getApiTeamError(teamErr));
  }

  /**
   * A library can still be readable as a public team after its agency access is gone, so the refusal
   * arrives on the members or jobs fetch instead; show it as not found rather than an unknown error.
   */
  private setContentError(err: Error | null) {
    if (!isAgencyAccessRefused(err))
      return this.setError();
    void this.store.agencyOperatorAccess.handleAccessRefused();
    return this.setError(error('NotFound'));
  }

  private unbindExternalListeners() {
    const { store } = this;
    store.videoDetailsWindow.unlisten(
      this.videoDetailsWindowListener);
  }

  reset() {
    this.setTeam(null);
    this.searchBarState.reset();
    this.tagSectionState.reset();
    this.jobCatalogSource.reset();
    this.store.searchService.resetSearchResults();
    this.store.accountGuidedTraining.pause();
    this.store.adminGuidedTraining.pause();
    this.isLoading = false;
    this.error = null;
    this.emit('Unmounted');
  }
}
